import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Mail, HeartCrack, ArrowLeft, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fun, clever warning copy — rotates each pageview so it doesn't feel canned.
const FAREWELL_LINES = [
  "Whoa, wait. Without our emails you'll be flying blind — no accountability nudges, no midweek pep talks, no 'hey, we saw you crush 3 days in a row' hype. Sure?",
  "Real talk: emails are how we tap you on the shoulder when you go quiet. Unsubscribe and future-you will have to be their own hype squad. Bold move.",
  "The emails are basically your accountability partner in your pocket. Break up with them and you'll be building GRIT solo. Doable — just not as fun.",
  "You'd be walking away from the exact nudges that catch you before a slip becomes a slide. We only email when there's a reason. Still want out?",
  "This is where we say: hey — the emails are how we celebrate wins, catch dips, and remind you why you started. Cool cool cool. Just checking you know.",
];

const BrandHeader = () => (
  <Link to="/" className="flex items-center gap-3 mb-6" data-testid="unsubscribe-brand-logo">
    <div className="w-10 h-10 bg-[#EFB342] rounded-xl flex items-center justify-center">
      <span className="text-black font-heading font-bold text-lg leading-none">Rx</span>
    </div>
    <div className="leading-tight">
      <span className="font-heading font-bold text-stone-900 text-base block">RxGRIT</span>
      <span className="text-stone-500 text-[10px] uppercase tracking-widest">by BUILD Nutrition</span>
    </div>
  </Link>
);

const UnsubscribePage = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('reviewing');
  const [submitting, setSubmitting] = useState(false);
  const [farewell] = useState(
    () => FAREWELL_LINES[Math.floor(Math.random() * FAREWELL_LINES.length)]
  );
  const [feedbackReason, setFeedbackReason] = useState(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('This unsubscribe link is missing its token. Try clicking it directly from the email.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/unsubscribe/info?token=${encodeURIComponent(token)}`);
        setInfo(res.data);
        if (res.data.already_opted_out) setStatus('opted_out');
      } catch (err) {
        setError(err.response?.data?.detail || 'This unsubscribe link is invalid or expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleUnsubscribe = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/unsubscribe/confirm`, { token });
      setStatus('opted_out');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not process. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResubscribe = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/unsubscribe/resubscribe`, { token });
      setStatus('resubscribed');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not process. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitFeedback = async () => {
    if (!feedbackReason) return;
    setFeedbackSubmitting(true);
    try {
      await axios.post(`${API}/unsubscribe/feedback`, {
        token,
        reason: feedbackReason,
        comment: feedbackComment.trim() || null,
      });
      setFeedbackDone(true);
    } catch (err) {
      setFeedbackDone(true);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F4F1] flex items-center justify-center px-4 text-stone-900">
        <Loader2 className="w-8 h-8 text-[#EFB342] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F5F4F1] flex items-center justify-center px-4 text-stone-900">
        <div className="max-w-md w-full bg-white border border-stone-200 rounded-3xl p-8 text-center shadow-sm">
          <BrandHeader />
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 mb-3 uppercase tracking-tight">Link expired</h1>
          <p className="text-stone-600 mb-6 leading-relaxed">{error}</p>
          <Link to="/login" className="inline-flex items-center gap-2 text-[#B8871E] hover:underline font-semibold">
            <ArrowLeft size={16} /> Back to RxGRIT
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F4F1] flex items-center justify-center px-4 py-12 text-stone-900">
      <div className="max-w-lg w-full bg-white border border-stone-200 rounded-3xl p-8 shadow-sm" data-testid="unsubscribe-card">
        <BrandHeader />

        {status === 'reviewing' && (
          <>
            <div className="flex items-start gap-3 mb-5">
              <HeartCrack className="text-[#B8871E] mt-1 flex-shrink-0" size={28} />
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 leading-tight uppercase tracking-tight">
                Sure you want to go dark on our emails?
              </h1>
            </div>
            <div className="bg-[#EFB342]/10 border border-[#EFB342]/30 rounded-2xl p-4 mb-6">
              <p className="text-stone-700 leading-relaxed text-sm md:text-base" data-testid="farewell-message">
                {farewell}
              </p>
            </div>
            {info?.email && (
              <p className="text-stone-500 text-sm mb-6 flex items-center gap-2">
                <Mail size={14} /> This will affect emails to <span className="text-stone-800 font-medium">{info.email}</span>
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleResubscribe}
                disabled={submitting}
                className="flex-1 bg-[#EFB342] hover:bg-[#D99F30] disabled:opacity-60 text-black font-semibold px-5 py-3.5 rounded-full transition-all uppercase tracking-wider text-sm"
                data-testid="keep-me-in-btn"
              >
                Nope, keep me in the loop
              </button>
              <button
                onClick={handleUnsubscribe}
                disabled={submitting}
                className="flex-1 bg-white hover:bg-stone-50 disabled:opacity-60 text-stone-700 font-medium px-5 py-3.5 rounded-full transition-all border border-stone-300 text-sm"
                data-testid="unsubscribe-btn"
              >
                {submitting ? 'Processing…' : 'Yes, unsubscribe me'}
              </button>
            </div>
            <p className="text-stone-500 text-xs mt-6 text-center leading-relaxed">
              You&apos;ll still get transactional emails (password resets, receipts). We&apos;ll never spam.
            </p>
          </>
        )}

        {status === 'opted_out' && (
          <div>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-stone-100 border border-stone-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="text-stone-500" size={28} />
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 mb-3 uppercase tracking-tight">You&apos;re unsubscribed</h1>
              <p className="text-stone-600 leading-relaxed">
                We hear you. No more RxGRIT emails from us.
              </p>
            </div>

            {!feedbackDone ? (
              <div className="bg-[#F0EBE1] border border-stone-200 rounded-2xl p-5 mb-6" data-testid="feedback-survey">
                <p className="text-stone-900 font-semibold mb-1">Quick one — what pushed you out?</p>
                <p className="text-stone-500 text-xs mb-4">Anonymous-ish to us, super useful for tuning what we send. Skip if you&apos;d rather not.</p>
                <div className="space-y-2 mb-4">
                  {[
                    { value: 'too_frequent', label: 'Too many emails' },
                    { value: 'not_relevant', label: 'Not relevant to me' },
                    { value: 'finished_goals', label: 'I finished my goals' },
                    { value: 'other', label: 'Other' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setFeedbackReason(opt.value)}
                      className={`w-full text-left px-4 py-3 rounded-xl border transition-all flex items-center gap-3 ${
                        feedbackReason === opt.value
                          ? 'bg-[#EFB342]/15 border-[#EFB342] text-stone-900'
                          : 'bg-white border-stone-200 text-stone-700 hover:border-stone-400'
                      }`}
                      data-testid={`feedback-reason-${opt.value}`}
                    >
                      <span className="font-medium">{opt.label}</span>
                      {feedbackReason === opt.value && (
                        <span className="ml-auto text-[#B8871E]">
                          <Check size={16} strokeWidth={3} />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                {feedbackReason === 'other' && (
                  <textarea
                    value={feedbackComment}
                    onChange={(e) => setFeedbackComment(e.target.value.slice(0, 500))}
                    placeholder="Tell us what's up (optional, 500 chars max)"
                    className="w-full bg-white border border-stone-300 text-stone-800 rounded-xl px-3 py-2 text-sm mb-4 min-h-[80px] focus:outline-none focus:border-[#EFB342] focus:ring-1 focus:ring-[#EFB342]"
                    data-testid="feedback-comment"
                  />
                )}
                <div className="flex gap-2">
                  <button
                    onClick={submitFeedback}
                    disabled={!feedbackReason || feedbackSubmitting}
                    className="flex-1 bg-[#EFB342] hover:bg-[#D99F30] disabled:opacity-40 disabled:cursor-not-allowed text-black font-semibold py-2.5 rounded-full text-sm uppercase tracking-wider"
                    data-testid="feedback-submit-btn"
                  >
                    {feedbackSubmitting ? 'Sending…' : 'Send feedback'}
                  </button>
                  <button
                    onClick={() => setFeedbackDone(true)}
                    className="px-4 py-2.5 text-stone-500 hover:text-stone-800 text-sm"
                    data-testid="feedback-skip-btn"
                  >
                    Skip
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-[#F0EBE1] border border-stone-200 rounded-2xl p-4 mb-6 text-center">
                <p className="text-stone-600 text-sm">Thanks — noted.</p>
              </div>
            )}

            <p className="text-stone-500 text-sm text-center mb-4">
              Change your mind? One-tap back in — no hard feelings.
            </p>
            <button
              onClick={handleResubscribe}
              disabled={submitting}
              className="w-full bg-[#EFB342] hover:bg-[#D99F30] disabled:opacity-60 text-black font-semibold px-5 py-3.5 rounded-full transition-all mb-4 uppercase tracking-wider text-sm"
              data-testid="resubscribe-btn"
            >
              {submitting ? 'Processing…' : "Actually, I want back in"}
            </button>
            <div className="text-center">
              <Link to="/login" className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-800 text-sm">
                <ArrowLeft size={14} /> Back to RxGRIT
              </Link>
            </div>
          </div>
        )}

        {status === 'resubscribed' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-[#EFB342]/20 border border-[#EFB342]/40 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="text-[#B8871E]" size={30} strokeWidth={3} />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 mb-3 uppercase tracking-tight">You&apos;re back. Let&apos;s go.</h1>
            <p className="text-stone-600 mb-6 leading-relaxed">
              We&apos;ll keep sending the good stuff — nudges, wins, and the occasional pep talk. Welcome back to the crew.
            </p>
            <Link to="/my-actions" className="inline-block bg-[#EFB342] hover:bg-[#D99F30] text-black font-semibold px-6 py-3 rounded-full uppercase tracking-wider text-sm">
              Take me to my dashboard
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnsubscribePage;
